import React, { useState, useEffect } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Loader2, AlertCircle, ArrowLeft, Eye, EyeOff, User, Info } from 'lucide-react';

interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

interface ValidateEmailResponse {
  is_tekion_user: boolean;
  has_account: boolean;
  display_name: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, redirectTo = '/' }) => {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Step state: 0 = email, 1 = password
  const [step, setStep] = useState(0);
  
  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // User info from email validation
  const [displayName, setDisplayName] = useState('');
  
  // Loading and error states
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Check for pre-filled email from navigation state (e.g., from password reset)
  useEffect(() => {
    const stateEmail = (location.state as { email?: string })?.email;
    if (stateEmail) {
      console.log('[LoginForm] Pre-filled email from navigation state:', stateEmail);
      setEmail(stateEmail);
      // Auto-validate the email and move to password step
      validatePrefilledEmail(stateEmail);
    }
  }, [location.state]);

  const validatePrefilledEmail = async (emailToValidate: string) => {
    setValidatingEmail(true);
    setError(null);
    setInfoMessage(null);
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/validate-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailToValidate }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.detail || 'Email validation failed');
        return;
      }

      const data: ValidateEmailResponse = await response.json();
      
      // If user has account, proceed to password step
      if (data.has_account) {
        setDisplayName(data.display_name);
        setStep(1);
      } else {
        setInfoMessage('No account found for this email. Please sign up first.');
      }
    } catch (error) {
      console.error('Email validation error:', error);
      setError('Failed to validate email. Please try again.');
    } finally {
      setValidatingEmail(false);
    }
  };

  const validateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfoMessage(null);
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    setValidatingEmail(true);
    try {
      const backendUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8002';
      const normalizedBase = backendUrl.replace(/\/+$/, '');
      
      const response = await fetch(`${normalizedBase}/auth/validate-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'This email is not in Tekion DMS, please ask your manager for help.');
        return;
      }

      const validationResult = data as ValidateEmailResponse;
      
      if (!validationResult.has_account) {
        // Tekion user but no account - show info message (they can click the Sign up button below)
        setInfoMessage('No account found for this email. Please sign up first.');
        return;
      }

      // Has account - proceed to password step
      setDisplayName(validationResult.display_name);
      setStep(1);
      
    } catch (error) {
      console.error('Email validation failed:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to validate email. Please try again.');
      }
    } finally {
      setValidatingEmail(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoggingIn(true);
    try {
      await signIn(email, password);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(redirectTo);
      }
    } catch (error) {
      console.error('Login failed:', error);
      // Since email is already validated, the error is most likely a wrong password
      let errorMessage = 'Incorrect password. Please try again.';
      if (error instanceof Error) {
        // Check if it's a different error (not password related)
        const msg = error.message.toLowerCase();
        if (msg.includes('password') || msg.includes('invalid') || msg.includes('credentials')) {
          errorMessage = 'Incorrect password. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      setError(errorMessage);
    } finally {
      setLoggingIn(false);
    }
  };

  const handleBack = () => {
    setStep(0);
    setPassword('');
    setError(null);
    setShowPassword(false);
  };

  const isLoading = validatingEmail || loggingIn;

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">
          {step === 0 ? 'Login' : `Welcome back${displayName ? `, ${displayName.split(' ')[0]}` : ''}!`}
        </CardTitle>
        <CardDescription>
          {step === 0 
            ? 'Enter your email to continue' 
            : 'Enter your password to access your account'
          }
        </CardDescription>
        
        {/* Step indicator */}
        <div className="flex gap-2 justify-center pt-2">
          <div className={`w-2 h-2 rounded-full transition-colors ${step === 0 ? 'bg-primary' : 'bg-gray-300'}`} />
          <div className={`w-2 h-2 rounded-full transition-colors ${step === 1 ? 'bg-primary' : 'bg-gray-300'}`} />
        </div>
      </CardHeader>
      
      {/* Sliding container - only hide horizontal overflow for slide animation */}
      <div className="overflow-x-hidden">
        <div 
          className="flex transition-transform duration-300 ease-in-out"
          style={{ transform: `translateX(-${step * 100}%)` }}
        >
          {/* Step 1: Email */}
          <div className="w-full flex-shrink-0">
            <form onSubmit={validateEmail}>
              <CardContent className="space-y-4">
                {/* Error message */}
                {error && step === 0 && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-md border border-red-200 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                
                {/* Info message */}
                {infoMessage && (
                  <div className="bg-blue-50 text-blue-700 text-sm p-3 rounded-md border border-blue-200 flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>{infoMessage}</p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null);
                      if (infoMessage) setInfoMessage(null);
                    }}
                    disabled={isLoading}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 mt-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                >
                  {validatingEmail ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Continue'
                  )}
                </Button>
                
                {/* Or divider */}
                <div className="relative flex items-center w-full">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="px-3 text-xs text-muted-foreground uppercase">or</span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="mb-2 w-full border border-primary text-primary hover:bg-primary/10"
                  onClick={() => navigate('/signup')}
                  disabled={isLoading}
                >
                  Sign up
                </Button>
              </CardFooter>
            </form>
          </div>
          
          {/* Step 2: Password */}
          <div className="w-full flex-shrink-0">
            <form onSubmit={handleLogin}>
              <CardContent className="space-y-4">
                {error && step === 1 && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-md border border-red-200 flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                
                {/* Email display */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{email}</p>
                    <button
                      type="button"
                      className="text-xs text-primary hover:underline"
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      Change email
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (error) setError(null);
                      }}
                      disabled={isLoading}
                      required
                      autoComplete="current-password"
                      autoFocus={step === 1}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-sm text-primary hover:underline"
                      onClick={() => navigate('/forgot-password')}
                      disabled={isLoading}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-3 mt-4">
                <div className="flex gap-2 w-full">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack}
                    disabled={isLoading}
                    className="px-3"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={isLoading}
                  >
                    {loggingIn ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Logging in...
                      </>
                    ) : (
                      'Log in'
                    )}
                  </Button>
                </div>
              </CardFooter>
            </form>
          </div>
        </div>
      </div>
    </Card>
  );
};
